'use client';

import { usePathname } from 'next/navigation';
import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Top-of-viewport progress bar that fires on internal navigation clicks.
 * Pattern: ramp to 80% on click, snap to 100% when pathname commits, fade
 * out, reset. Same approach NProgress / GitHub / Linear use.
 */
export function NavProgress() {
  const pathname = usePathname();
  const [progress, setProgress] = useState(0);
  const [visible, setVisible] = useState(false);
  const rampTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimers = useCallback(() => {
    if (rampTimer.current) clearTimeout(rampTimer.current);
    if (hideTimer.current) clearTimeout(hideTimer.current);
  }, []);

  const start = useCallback(() => {
    clearTimers();
    setVisible(true);
    setProgress(15);
    // Ease toward 80% over ~600ms in two steps so it feels alive even on
    // a slow dev compile.
    rampTimer.current = setTimeout(() => {
      setProgress(50);
      rampTimer.current = setTimeout(() => setProgress(80), 300);
    }, 150);
  }, [clearTimers]);

  const finish = useCallback(() => {
    clearTimers();
    setProgress(100);
    hideTimer.current = setTimeout(() => {
      setVisible(false);
      hideTimer.current = setTimeout(() => setProgress(0), 200);
    }, 150);
  }, [clearTimers]);

  // Listen for clicks on internal anchors anywhere in the document.
  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (e.defaultPrevented) return;
      if (e.button !== 0) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

      const target = e.target as HTMLElement | null;
      const anchor = target?.closest('a');
      if (!anchor) return;

      const href = anchor.getAttribute('href');
      if (!href) return;
      if (anchor.target && anchor.target !== '_self') return;
      if (anchor.hasAttribute('download')) return;
      if (
        href.startsWith('http://') ||
        href.startsWith('https://') ||
        href.startsWith('mailto:') ||
        href.startsWith('tel:') ||
        href.startsWith('#')
      )
        return;

      const url = new URL(href, window.location.href);
      if (url.pathname === window.location.pathname) return;

      start();
    }

    document.addEventListener('click', onClick, { capture: true });
    return () => document.removeEventListener('click', onClick, { capture: true });
  }, [start]);

  // When the pathname changes, the new route has committed.
  const firstRender = useRef(true);
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    finish();
  }, [pathname, finish]);

  useEffect(() => () => clearTimers(), [clearTimers]);

  return (
    <div
      aria-hidden="true"
      className="pointer-events-none fixed inset-x-0 top-0 z-[60] h-[2px] md:h-[3px]"
      style={{
        opacity: visible ? 1 : 0,
        transition: visible
          ? 'opacity 100ms ease-out'
          : 'opacity 200ms ease-out 100ms',
      }}
    >
      <div
        className="h-full bg-[var(--accent)]"
        style={{
          width: `${progress}%`,
          transition: 'width 200ms ease-out',
        }}
      />
    </div>
  );
}
